import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/api/client'
import { formatApiError } from '@/utils/httpErrors'
import { localeDefaultCurrency } from '@/lib/currency'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { Eye, EyeOff } from 'lucide-react'
import { PRODUCT, PRODUCT_KEY } from '@/lib/productIdentity'

// ── Types ──────────────────────────────────────────────────────────────────────

interface DealLink {
  seller_link: string
  buyer_link: string
}

// ── Component ──────────────────────────────────────────────────────────────────

export function GettingStarted() {
  const navigate = useNavigate()
  const { user, refreshToken } = useAuth()

  // Redirect if onboarding already done — but PLAT-FIRST-LOGIN-2 R1: never
  // while a forced password change is still pending (must_change_password),
  // or this is exactly the page an onboarded-but-flagged user gets thrown
  // off of. And never a hard-coded '/dashboard' — Valuation/Attest builds
  // don't have that route (same PRODUCT_KEY switch as the step-1 handler
  // below).
  useEffect(() => {
    if (user?.onboarding_complete && !user?.must_change_password) {
      const home = PRODUCT_KEY === 'valuation' ? '/valuation'
        : PRODUCT_KEY === 'attest' ? '/attest/letters'
        : '/dashboard'
      navigate(home, { replace: true })
    }
  }, [user, navigate])

  const [step, setStep] = useState(1)
  const totalSteps = 3

  // Step 1 — Change password
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwError, setPwError] = useState<string | null>(null)
  const [pwSaving, setPwSaving] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Step 2 — Create first deal
  const [dealName, setDealName] = useState('')
  const [loi, setLoi] = useState('')
  const [dealCreating, setDealCreating] = useState(false)
  const [dealError, setDealError] = useState<string | null>(null)
  const [dealLinks, setDealLinks] = useState<DealLink | null>(null)

  // Step 3 — Copy links state
  const [copied, setCopied] = useState<'seller' | 'buyer' | null>(null)

  const nextStep = () => setStep(s => Math.min(s + 1, totalSteps))

  // ── Step 1 handlers ──────────────────────────────────────────────────────────

  const handleChangePassword = async () => {
    setPwError(null)
    if (!currentPassword) {
      setPwError('Enter your temporary password.')
      return
    }
    if (newPassword.length < 8) {
      setPwError('Password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setPwError('Passwords do not match.')
      return
    }
    setPwSaving(true)
    try {
      await apiClient.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      })
      // PLAT-FIRST-LOGIN-1 R4: the just-changed password moved
      // password_changed_at past the access token's iat (QOE-AUTH-RESET-1
      // in apps/api/auth.py), so the stale cookie would 401 on the very next
      // request. refreshToken() mints a fresh-iat token via /auth/refresh
      // before we navigate anywhere.
      await refreshToken()
      if (PRODUCT_KEY === 'valuation' || PRODUCT_KEY === 'attest') {
        // R3: Valuation/Attest have no steps 2-3 — this field IS onboarding.
        try {
          await apiClient.patch('/users/me/onboarding-complete')
        } catch {
          // Best-effort — don't block navigation
        }
        navigate(PRODUCT_KEY === 'valuation' ? '/valuation' : '/attest/letters')
      } else {
        nextStep()
      }
    } catch (err: unknown) {
      // R2: the raw 401/422 envelope never renders on this screen.
      const e = err as { response?: { status?: number } }
      if (e?.response?.status === 401) {
        setPwError("That temporary password isn't right.")
      } else {
        setPwError(formatApiError(err))
      }
    } finally {
      setPwSaving(false)
    }
  }

  // ── Step 2 handlers ──────────────────────────────────────────────────────────

  const handleCreateDeal = async () => {
    setDealError(null)
    setDealCreating(true)
    try {
      const res = await apiClient.post<{
        deal_id: string
        seller_token: string
        buyer_token: string
      }>('/qoe/deals', {
        business_name: dealName,
        analyst_name: user?.name ?? '',
        // CURRENCY-1: seed the reporting currency from the CPA's own browser
        // locale. Editable afterwards on the normalization workspace.
        currency: localeDefaultCurrency(),
      })
      const base = window.location.origin
      setDealLinks({
        seller_link: `${base}/seller?token=${res.data.seller_token}`,
        buyer_link: `${base}/buyer?token=${res.data.buyer_token}`,
      })
      nextStep()
    } catch (err: unknown) {
      const e = err as { response?: { status?: number } }
      if (e?.response?.status === 409) {
        setDealError('A deal with that name already exists.')
      } else {
        setDealError(formatApiError(err))
      }
    } finally {
      setDealCreating(false)
    }
  }

  const handleSkipDeal = () => {
    setDealLinks(null)
    nextStep()
  }

  // ── Step 3 handlers ──────────────────────────────────────────────────────────

  const copyToClipboard = (text: string, type: 'seller' | 'buyer') => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type)
      setTimeout(() => setCopied(null), 2000)
    })
  }

  const handleDone = async () => {
    try {
      await apiClient.patch('/users/me/onboarding-complete')
    } catch {
      // Best-effort — don't block navigation
    }
    navigate('/dashboard')
  }

  const stepLabel = step === 1 ? 'Set Password' : step === 2 ? 'Create Deal' : 'Send Links'
  const mustChangePassword = user?.must_change_password ?? false
  // PRODUCT-IDENTITY-1 (I3): preserve QoE's exact pre-existing text ("the QoE
  // Platform"), same reasoning as TosGate.tsx — see that file's comment.
  const welcomeProductName = PRODUCT_KEY === 'qoe' ? 'the QoE Platform' : PRODUCT.headline

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <h1 className="text-2xl font-semibold mb-1" style={{ color: 'var(--color-primary)' }}>
        Welcome to {welcomeProductName}
      </h1>
      <p className="text-sm text-gray-500 mb-6">Let's get you set up in a few quick steps.</p>

      {/* Progress — R3: Valuation/Attest have no steps 2-3, so no progress
          bar at all (this field IS the whole onboarding for those builds). */}
      {PRODUCT_KEY === 'qoe' && (
        <div className="mb-8">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Step {step} of {totalSteps}</span>
            <span>{stepLabel}</span>
          </div>
          <Progress value={(step / totalSteps) * 100} />
        </div>
      )}

      {/* ── Step 1: Change Password ────────────────────────────────────────── */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Set Your Password</CardTitle>
            <CardDescription>
              {mustChangePassword
                ? 'Your account requires a password change before you can continue.'
                : 'Set a secure password for your account.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>Temporary password</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  placeholder="Your temporary password"
                  style={{ paddingRight: '38px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(v => !v)}
                  aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: '10px', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer', padding: 0,
                    color: '#6b7280', display: 'flex', alignItems: 'center',
                  }}
                >
                  {showCurrentPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <p className="text-xs text-gray-400">The password you just signed in with.</p>
            </div>
            <div className="space-y-1.5">
              <Label>New Password</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  style={{ paddingRight: '38px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(v => !v)}
                  aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: '10px', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer', padding: 0,
                    color: '#6b7280', display: 'flex', alignItems: 'center',
                  }}
                >
                  {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Confirm Password</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  style={{ paddingRight: '38px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(v => !v)}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: '10px', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer', padding: 0,
                    color: '#6b7280', display: 'flex', alignItems: 'center',
                  }}
                >
                  {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            {pwError && (
              <Alert variant="destructive">
                <AlertDescription>{pwError}</AlertDescription>
              </Alert>
            )}
            <Button
              className="w-full"
              style={{ backgroundColor: 'var(--color-primary)' }}
              disabled={!currentPassword || !newPassword || !confirmPassword || pwSaving}
              onClick={handleChangePassword}
            >
              {pwSaving ? 'Saving…' : 'Set Password & Continue →'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Step 2: Create First Deal ──────────────────────────────────────── */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Create Your First Deal</CardTitle>
            <CardDescription>
              Enter a target company name to create your first engagement. You can skip this and create one from the Dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>Target Company Name</Label>
              <Input
                value={dealName}
                onChange={e => setDealName(e.target.value)}
                placeholder="Lone Star HVAC Services LLC"
              />
            </div>
            <div className="space-y-1.5">
              <Label>LOI Amount (USD)</Label>
              <Input
                value={loi}
                onChange={e => setLoi(e.target.value)}
                placeholder="1,250,000"
              />
            </div>
            {dealError && (
              <Alert variant="destructive">
                <AlertDescription>{dealError}</AlertDescription>
              </Alert>
            )}
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={handleSkipDeal}
              >
                Skip for now
              </Button>
              <Button
                className="flex-1"
                style={{ backgroundColor: 'var(--color-primary)' }}
                disabled={!dealName || dealCreating}
                onClick={handleCreateDeal}
              >
                {dealCreating ? 'Creating…' : 'Create Deal →'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Step 3: Copy & Send Links ──────────────────────────────────────── */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Share Access Links</CardTitle>
            <CardDescription>
              {dealLinks
                ? 'Send these links to your seller and buyer. Each link is unique and authenticated — no login required.'
                : 'You skipped deal creation. You can generate links from the Dashboard after creating a deal.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {dealLinks ? (
              <>
                <div className="space-y-4">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Seller Portal Link
                  </Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      readOnly
                      value={dealLinks.seller_link}
                      className="text-xs font-mono bg-gray-50"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => copyToClipboard(dealLinks.seller_link, 'seller')}
                    >
                      {copied === 'seller' ? '✓ Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Buyer Portal Link
                  </Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      readOnly
                      value={dealLinks.buyer_link}
                      className="text-xs font-mono bg-gray-50"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => copyToClipboard(dealLinks.buyer_link, 'buyer')}
                    >
                      {copied === 'buyer' ? '✓ Copied' : 'Copy'}
                    </Button>
                  </div>
                </div>

                <p className="text-xs text-gray-400">
                  Links are also available on the Dashboard at any time.
                </p>
              </>
            ) : (
              <p className="text-sm text-gray-500">
                Head to the Dashboard to create a deal and generate portal links.
              </p>
            )}

            <Button
              className="w-full mt-2"
              style={{ backgroundColor: 'var(--color-primary)' }}
              onClick={handleDone}
            >
              Go to Dashboard →
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
